import { pluralCategory, type Locale } from './index';

/**
 * Every number in either application goes through one of these. None of them
 * hardcodes a separator or a unit word: `9072` groups as `9.072` in pt-BR and
 * `9,072` in en, entirely through `Intl.NumberFormat`, and a caller that
 * needs a fixed string is asking the wrong layer.
 */

/** Provider credits, grouped by locale. No fixed decimals: credits are
 *  counted whole, so `Intl.NumberFormat`'s default (no explicit fraction
 *  digits) is exactly the grouping behaviour wanted. `locale` is required:
 *  a defaulted locale is a silent pt-BR fallback that no test catches. */
export function credits(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** A projection, never a ledger figure: `Intl`'s own compact notation
 *  (`125400` reads as "125,4 mil" in pt-BR, "125.4K" in en). One decimal
 *  digit, the same cap `percent` and `interval` use, so a compacted figure
 *  never carries more precision than the projection it rounds. A figure
 *  someone can reconcile against a ledger (an actual, a trailing total)
 *  renders through `credits` instead — full digits, never compacted. */
export function compact(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

/** A plain grouped integer — same grouping as `credits`, with the fractional
 *  part forced off even if the input is not a whole number. */
export function integer(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/** A percentage-point change ("+24%", "-5%", "0%") for a figure like `reach_delta_pct` that is
 *  already a percentage (20 meaning 20%), not a ratio — divided by 100 before Intl's `percent`
 *  style multiplies it back out. `signDisplay: 'exceptZero'` is the native formatter option for a
 *  delta chip, so no sign is hand-built. */
export function percentDelta(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    signDisplay: 'exceptZero',
    maximumFractionDigits: 1,
  }).format(value / 100);
}

/** A ratio (0..1) as a percentage, one decimal place. Zero decimals rounds
 *  §6.6's worked 3.5% utilisation figure to 4%, which reads as a different,
 *  better number than the one the estimator computed; more than one decimal
 *  reads as false precision for a figure this coarse. One decimal is the
 *  deliberate middle. */
export function percent(ratio: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(
    ratio,
  );
}

type IntervalUnit = 'd' | 'h' | 'min' | 's';

// pt-BR and en abbreviate identically today ("min", "h", "d"), so this
// Record<Locale, ...> buys nothing yet. Kept keyed by locale rather than a
// flat map anyway: the day the abbreviations diverge (a locale that wants
// "hr" instead of "h", say) is the day this table needs an entry, not a
// rewrite, and `interval` below already threads `locale` through to it.
const INTERVAL_UNIT_LABEL: Record<Locale, Record<IntervalUnit, string>> = {
  'pt-BR': { d: 'd', h: 'h', min: 'min', s: 's' },
  en: { d: 'd', h: 'h', min: 'min', s: 's' },
};

/** "6 h", "2 d", "30 min" — an interval as a tenant reads it, not seconds.
 *  Picks the largest whole unit that fits (days, then hours, then minutes,
 *  then seconds) and lets `Intl.NumberFormat`'s own `maximumFractionDigits`
 *  round to one decimal within that unit, so an inexact interval still
 *  renders (`325` seconds is `5.4 min`) rather than falling through to a raw
 *  second count. */
export function interval(seconds: number, locale: Locale): string {
  const abs = Math.abs(seconds);
  const [divisor, unit]: [number, IntervalUnit] =
    abs >= 86400 ? [86400, 'd'] : abs >= 3600 ? [3600, 'h'] : abs >= 60 ? [60, 'min'] : [1, 's'];
  const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    seconds / divisor,
  );
  return `${formatted} ${INTERVAL_UNIT_LABEL[locale][unit]}`;
}

const DAY_WORD: Record<Locale, { one: string; other: string }> = {
  'pt-BR': { one: 'dia', other: 'dias' },
  en: { one: 'day', other: 'days' },
};

/** A count of days, spelled out ("1 day" / "30 days"), for the tracking
 *  period rather than a fetch interval — see `interval` for the latter. */
export function days(value: number, locale: Locale): string {
  const category = pluralCategory(value, locale);
  const word = category === 'one' ? DAY_WORD[locale].one : DAY_WORD[locale].other;
  return `${integer(value, locale)} ${word}`;
}
